// Auth store — the client's single entry point for authentication against the
// server API. Mirrors the server routes in server/routes/auth.ts. Each call
// returns the parsed JSON ({ user } / { ok } / { devToken }) or throws ApiError;
// callers surface err.message to the user, matching the old string-return contract.
//
// This module is intentionally side-effect-free (no React, no localStorage). The
// App wires it in: on boot it calls me() to hydrate the session, and login/
// register/logout/changePassword replace the localStorage auth once usingApi().

import { api } from '../api';
import { beaconQueue } from '../beaconQueue';

export const authStore = {
  // Resolve the current session as { user, impersonatedBy }, or null if not signed in.
  async me() {
    try {
      const { user, impersonatedBy } = await api.get('/api/auth/me');
      return { user, impersonatedBy: impersonatedBy || null };
    } catch (err) {
      if (err.status === 401) return null;
      throw err;
    }
  },

  // Admin support access: become `userId` (server switches the session + remembers
  // the admin so we can exit back). Returns the target user.
  async impersonate(userId, note) {
    const { user } = await api.post('/api/auth/impersonate', { userId, note });
    return user;
  },
  // Restore the admin's own session. Returns the admin user.
  async exitImpersonation() {
    const { user } = await api.post('/api/auth/exit-impersonation');
    return user;
  },

  async login(username, password) {
    const { user } = await api.post('/api/auth/login', { username, password });
    return user;
  },

  // opts: { userName, password, email?, orgId?, mlsId?, invite?, appOrigin? }
  // Returns the raw response: { user } for an immediate account, or { pending, email }
  // when an anonymous public sign-up needs email verification first.
  async register(opts) {
    return api.post('/api/auth/register', opts);
  },

  // Resolve a pending sign-up token → { valid, email, username } (for the verify screen).
  async verifyInfo(token) {
    return api.get(`/api/auth/verify-info?token=${encodeURIComponent(token)}`);
  },
  // Complete a verified sign-up: creates the account, signs in, returns the user.
  async verifySignup(token) {
    const { user } = await api.post('/api/auth/verify-signup', { token });
    return user;
  },

  async logout() {
    await api.post('/api/auth/logout');
  },

  async changePassword(newPassword) {
    const { user } = await api.post('/api/auth/change-password', { newPassword });
    return user;
  },

  // Persist profile fields (alias/phone/photo/licenseId/email, and optionally
  // username/password) to the central User record. Returns the updated user.
  // Queues the patch for an unload-beacon first so it isn't lost if the user refreshes
  // mid-save on iOS; clears it once the normal request confirms.
  async updateProfile(patch) {
    beaconQueue.set('profile', '/api/auth/me', patch);
    const { user } = await api.patch('/api/auth/me', patch);
    beaconQueue.clear('profile');
    return user;
  },

  // Returns { ok, devToken? } — devToken is present only outside production.
  async forgot(identifier) {
    return api.post('/api/auth/forgot', { username: identifier, email: identifier });
  },

  async reset(token, newPassword) {
    return api.post('/api/auth/reset', { token, newPassword });
  },
};
